/**
 * @fileoverview Route definitions for Planthra SaaS
 * @module router/routes
 */
import type { RouteRecordRaw } from 'vue-router'

// Layouts
const DefaultLayout = () => import('@/layouts/DefaultLayout.vue')
const AuthLayout = () => import('@/layouts/AuthLayout.vue')
const AdminLayout = () => import('@/layouts/AdminLayout.vue')
const MarketingLayout = () => import('@/layouts/MarketingLayout.vue')
const WelcomeLayout = () => import('@/layouts/WelcomeLayout.vue')

// Public views (no auth required - marketplace pages)
const LandingPage = () => import('@/views/public/LandingPage.vue')
const PricingPage = () => import('@/views/public/PricingPage.vue')
const PreviewPage = () => import('@/views/public/PreviewPage.vue')

// Auth views
const LoginView = () => import('@/views/auth/LoginView.vue')
const SignupView = () => import('@/views/auth/SignupView.vue')
const ForgotPasswordView = () => import('@/views/auth/ForgotPasswordView.vue')
const AcceptInviteView = () => import('@/views/auth/AcceptInviteView.vue')

// Main views
const DashboardView = () => import('@/views/DashboardView.vue')

// Welcome views
const WelcomeWizard = () => import('@/views/welcome/WelcomeWizard.vue')

// App views
const WorkspaceHomeView = () => import('@/views/app/WorkspaceHomeView.vue')

// Tool views
const QRToolView = () => import('@/views/tools/QRToolView.vue')
const MeetingsToolView = () => import('@/views/tools/MeetingsToolView.vue')
const ProjectsToolView = () => import('@/views/tools/ProjectsToolView.vue')
const SurveysToolView = () => import('@/views/tools/SurveysToolView.vue')
const TeamStatusToolView = () => import('@/views/tools/TeamStatusToolView.vue')

// Advocacy views
const ReferralView = () => import('@/views/advocacy/ReferralView.vue')

// Settings views
const SettingsLayout = () => import('@/views/settings/SettingsLayout.vue')
const ProfileSettings = () => import('@/views/settings/ProfileSettings.vue')
const TeamSettings = () => import('@/views/settings/TeamSettings.vue')
const BrandingSettings = () => import('@/views/settings/BrandingSettings.vue')
const BillingSettings = () => import('@/views/settings/BillingSettings.vue')
const SecuritySettings = () => import('@/views/settings/SecuritySettings.vue')

// Enterprise views (owner only - enterprise plan)
const SSOSettingsView = () => import('@/views/enterprise/SSOSettingsView.vue')
const CustomDomainsView = () => import('@/views/enterprise/CustomDomainsView.vue')
const SLADashboardView = () => import('@/views/enterprise/SLADashboardView.vue')
const ComplianceDashboardView = () => import('@/views/enterprise/ComplianceDashboardView.vue')

// Admin views (super admin only)
const AdminDashboardView = () => import('@/views/admin/AdminDashboardView.vue')
const AdminTenantsView = () => import('@/views/admin/TenantsView.vue')
const AdminUsersView = () => import('@/views/admin/UsersView.vue')
const AdminBillingView = () => import('@/views/admin/BillingView.vue')
const AdminAuditView = () => import('@/views/admin/AuditView.vue')
const AdminFeaturesView = () => import('@/views/admin/FeaturesView.vue')
const AdminHealthView = () => import('@/views/admin/HealthView.vue')
const AdminSettingsView = () => import('@/views/admin/SettingsView.vue')

// Developer views
const DeveloperLayout = () => import('@/layouts/DeveloperLayout.vue')
const DevBrandingTab = () => import('@/views/developer/tabs/BrandingTab.vue')
const DevFeaturesTab = () => import('@/views/developer/tabs/FeatureFlagsTab.vue')
const DevDebugTab = () => import('@/views/developer/tabs/DebugToolsTab.vue')
const DevEnvironmentTab = () => import('@/views/developer/tabs/EnvironmentTab.vue')
const DevSeedingTab = () => import('@/views/developer/tabs/DatabaseSeedingTab.vue')
const DevPerformanceTab = () => import('@/views/developer/tabs/PerformanceTab.vue')
const DevPricingTab = () => import('@/views/developer/tabs/PricingTab.vue')
const DevAccountsTab = () => import('@/views/developer/tabs/AccountsTab.vue')
const DevTenantsTab = () => import('@/views/developer/tabs/TenantsTab.vue')

// Error views
const NotFoundView = () => import('@/views/errors/NotFoundView.vue')
const ForbiddenView = () => import('@/views/errors/ForbiddenView.vue')
const SuspendedView = () => import('@/views/errors/SuspendedView.vue')
const InvalidSubdomainView = () =>
  import('@/views/errors/InvalidSubdomainView.vue')
const UnauthorizedTenantView = () =>
  import('@/views/errors/UnauthorizedTenantView.vue')

export const routes: RouteRecordRaw[] = [
  // ===========================================
  // PUBLIC ROUTES - NO AUTH REQUIRED
  // These are the marketplace/marketing pages
  // ===========================================
  {
    path: '/',
    component: MarketingLayout,
    children: [
      {
        path: '',
        name: 'home',
        component: LandingPage,
        meta: { title: 'Multi-Tenant SaaS Platform' }
      },
      {
        path: 'pricing',
        name: 'pricing',
        component: PricingPage,
        meta: { title: 'Pricing' }
      },
      {
        path: 'about',
        name: 'about',
        component: () => import('@/views/public/AboutPage.vue'),
        meta: { title: 'About' }
      },
      {
        path: 'contact',
        name: 'contact',
        component: () => import('@/views/public/ContactPage.vue'),
        meta: { title: 'Contact Sales' }
      },
      {
        path: 'preview',
        name: 'preview',
        component: PreviewPage,
        meta: { title: 'Preview Your Workspace' }
      }
    ]
  },

  // Legacy redirects
  { path: '/login', redirect: '/auth/login' },
  { path: '/signup', redirect: '/auth/signup' },
  { path: '/forgot-password', redirect: '/auth/forgot-password' },

  // Auth routes (no auth required)
  {
    path: '/auth',
    component: AuthLayout,
    meta: { layout: 'auth' },
    children: [
      {
        path: 'login',
        name: 'login',
        component: LoginView,
        meta: { title: 'Sign In' }
      },
      {
        path: 'signup',
        name: 'signup',
        component: SignupView,
        meta: { title: 'Create Account' }
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: ForgotPasswordView,
        meta: { title: 'Reset Password' }
      },
      {
        path: 'invite/:token',
        name: 'accept-invite',
        component: AcceptInviteView,
        meta: { title: 'Accept Invitation' }
      },
      {
        path: 'onboarding',
        name: 'onboarding',
        component: () => import('@/views/auth/OnboardingView.vue'),
        meta: {
          title: 'Complete Setup',
          requiresAuth: true
        }
      }
    ]
  },

  // ===========================================
  // AUTHENTICATED ROUTES - REQUIRES LOGIN
  // Tenant dashboard and settings
  // ===========================================
  {
    path: '/app',
    component: DefaultLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: DashboardView,
        meta: { title: 'Dashboard' }
      },
      {
        path: 'home',
        name: 'workspace-home',
        component: WorkspaceHomeView,
        meta: { title: 'Welcome Home' }
      },
      // Tool routes
      {
        path: 'tools/qr',
        name: 'tools-qr',
        component: QRToolView,
        meta: { title: 'QR Codes' }
      },
      {
        path: 'tools/meetings',
        name: 'tools-meetings',
        component: MeetingsToolView,
        meta: { title: 'Meetings' }
      },
      {
        path: 'tools/projects',
        name: 'tools-projects',
        component: ProjectsToolView,
        meta: { title: 'Projects' }
      },
      {
        path: 'tools/surveys',
        name: 'tools-surveys',
        component: SurveysToolView,
        meta: { title: 'Surveys' }
      },
      {
        path: 'tools/team-status',
        name: 'tools-team-status',
        component: TeamStatusToolView,
        meta: { title: 'Team Status' }
      },
      // Referral program
      {
        path: 'referral',
        name: 'referral',
        component: ReferralView,
        meta: { title: 'Refer & Earn' }
      },
      // Settings with sub-routes
      {
        path: 'settings',
        component: SettingsLayout,
        meta: { title: 'Settings' },
        children: [
          {
            path: '',
            redirect: '/app/settings/profile'
          },
          {
            path: 'profile',
            name: 'settings-profile',
            component: ProfileSettings,
            meta: { title: 'Profile' }
          },
          {
            path: 'team',
            name: 'settings-team',
            component: TeamSettings,
            meta: { title: 'Team' }
          },
          {
            path: 'branding',
            name: 'settings-branding',
            component: BrandingSettings,
            meta: {
              title: 'Branding',
              requiredRole: 'owner'
            }
          },
          {
            path: 'billing',
            name: 'settings-billing',
            component: BillingSettings,
            meta: {
              title: 'Billing',
              requiredRole: 'owner'
            }
          },
          {
            path: 'security',
            name: 'settings-security',
            component: SecuritySettings,
            meta: {
              title: 'Security',
              requiredRole: 'owner'
            }
          },
          // Enterprise settings (requires enterprise plan)
          {
            path: 'enterprise/sso',
            name: 'settings-sso',
            component: SSOSettingsView,
            meta: {
              title: 'SSO Configuration',
              requiredRole: 'owner',
              requiredFeature: 'sso'
            }
          },
          {
            path: 'enterprise/domains',
            name: 'settings-domains',
            component: CustomDomainsView,
            meta: {
              title: 'Custom Domains',
              requiredRole: 'owner',
              requiredFeature: 'customDomains'
            }
          },
          {
            path: 'enterprise/sla',
            name: 'settings-sla',
            component: SLADashboardView,
            meta: {
              title: 'SLA Dashboard',
              requiredRole: 'owner',
              requiredFeature: 'slaManagement'
            }
          },
          {
            path: 'enterprise/compliance',
            name: 'settings-compliance',
            component: ComplianceDashboardView,
            meta: {
              title: 'Compliance',
              requiredRole: 'owner',
              requiredFeature: 'compliance'
            }
          }
        ]
      },
      // Legacy routes redirect to settings
      { path: 'team', redirect: '/app/settings/team' },
      { path: 'branding', redirect: '/app/settings/branding' },
      { path: 'billing', redirect: '/app/settings/billing' }
    ]
  },

  // ===========================================
  // WELCOME WIZARD - MINIMAL LAYOUT (NO SIDEBAR)
  // Separate layout for distraction-free onboarding
  // ===========================================
  {
    path: '/app/welcome',
    component: WelcomeLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'welcome',
        component: WelcomeWizard,
        meta: { title: 'Welcome' }
      }
    ]
  },

  // ===========================================
  // DEVELOPER ROUTES - PLATFORM CONFIGURATION
  // Developer tools and debugging (requires developer email)
  // ===========================================
  {
    path: '/developer',
    component: DeveloperLayout,
    meta: {
      title: 'Developer Settings',
      requiresAuth: true,
      requiresDeveloper: true
    },
    children: [
      {
        path: '',
        redirect: '/developer/branding'
      },
      {
        path: 'branding',
        name: 'dev-branding',
        component: DevBrandingTab,
        meta: { title: 'Branding' }
      },
      {
        path: 'features',
        name: 'dev-features',
        component: DevFeaturesTab,
        meta: { title: 'Feature Flags' }
      },
      {
        path: 'debug',
        name: 'dev-debug',
        component: DevDebugTab,
        meta: { title: 'Debug Tools' }
      },
      {
        path: 'environment',
        name: 'dev-environment',
        component: DevEnvironmentTab,
        meta: { title: 'Environment' }
      },
      {
        path: 'seeding',
        name: 'dev-seeding',
        component: DevSeedingTab,
        meta: { title: 'Database Seeding' }
      },
      {
        path: 'performance',
        name: 'dev-performance',
        component: DevPerformanceTab,
        meta: { title: 'Performance' }
      },
      {
        path: 'pricing',
        name: 'dev-pricing',
        component: DevPricingTab,
        meta: { title: 'Pricing Configuration' }
      },
      {
        path: 'accounts',
        name: 'dev-accounts',
        component: DevAccountsTab,
        meta: { title: 'Accounts Management' }
      },
      {
        path: 'tenants',
        name: 'dev-tenants',
        component: DevTenantsTab,
        meta: { title: 'Tenants Management' }
      }
    ]
  },

  // Admin routes (super admin only)
  {
    path: '/admin',
    component: AdminLayout,
    meta: {
      requiresAuth: true,
      requiresSuperAdmin: true,
      layout: 'admin'
    },
    children: [
      {
        path: '',
        name: 'admin',
        component: AdminDashboardView,
        meta: { title: 'Dashboard' }
      },
      {
        path: 'tenants',
        name: 'admin-tenants',
        component: AdminTenantsView,
        meta: { title: 'Tenants' }
      },
      {
        path: 'tenants/:tenantId',
        name: 'admin-tenant-detail',
        component: () => import('@/views/admin/TenantDetailView.vue'),
        meta: { title: 'Tenant Details' }
      },
      {
        path: 'users',
        name: 'admin-users',
        component: AdminUsersView,
        meta: { title: 'Users' }
      },
      {
        path: 'billing',
        name: 'admin-billing',
        component: AdminBillingView,
        meta: { title: 'Billing' }
      },
      {
        path: 'features',
        name: 'admin-features',
        component: AdminFeaturesView,
        meta: { title: 'Feature Flags' }
      },
      {
        path: 'audit',
        name: 'admin-audit',
        component: AdminAuditView,
        meta: { title: 'Audit Logs' }
      },
      {
        path: 'health',
        name: 'admin-health',
        component: AdminHealthView,
        meta: { title: 'Health' }
      },
      {
        path: 'settings',
        name: 'admin-settings',
        component: AdminSettingsView,
        meta: { title: 'Settings' }
      }
    ]
  },

  // Error routes
  {
    path: '/suspended',
    name: 'suspended',
    component: SuspendedView,
    meta: { title: 'Account Suspended' }
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: ForbiddenView,
    meta: { title: 'Access Denied' }
  },
  {
    path: '/invalid-subdomain',
    name: 'invalid-subdomain',
    component: InvalidSubdomainView,
    meta: { title: 'Workspace Not Found' }
  },
  {
    path: '/unauthorized-tenant',
    name: 'unauthorized-tenant',
    component: UnauthorizedTenantView,
    meta: { title: 'Access Denied' }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: { title: 'Page Not Found' }
  }
]

// Type augmentation for route meta
declare module 'vue-router' {
  interface RouteMeta {
    /** Requires authenticated user */
    requiresAuth?: boolean
    /** Requires super admin role (platform admin) */
    requiresSuperAdmin?: boolean
    /** Requires tenant context to be loaded */
    requiresTenant?: boolean
    /** Requires developer email (platform developer tools) */
    requiresDeveloper?: boolean
    /** Minimum tenant role required: 'viewer' | 'editor' | 'admin' | 'owner' */
    requiredRole?: 'viewer' | 'editor' | 'admin' | 'owner'
    /** Permission string required (e.g., 'manage:users') */
    requiredPermission?: string
    /** Feature flag key that must be enabled */
    requiredFeature?: string
    /** Page title for document.title */
    title?: string
    /** Layout variant for styling */
    layout?: 'default' | 'auth' | 'admin'
  }
}
